import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  BarChart3, Check, ChevronRight, ChevronsUpDown, FileText, FolderKanban,
  Globe, Inbox, LayoutDashboard, Lightbulb, ListChecks, LogOut, Mail,
  Menu, Moon, Newspaper, Plug, Send, Settings, Sun, Users, X,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useProjects, useSession } from '../../hooks/queries';
import type { ProjectKind } from '../../types/api';
import { auth } from '../../lib/api';
import { cn, initials } from '../../lib/utils';
import { Button } from '../ui/primitives';
import { Tooltip } from '../ui/overlays';

const useTheme = () => {
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem('rentx.theme');
    return stored ? stored === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('rentx.theme', dark ? 'dark' : 'light');
  }, [dark]);
  return { dark, toggle: () => setDark((current) => !current) };
};

const NAV_GLOBAL = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/projects', label: 'Projects', icon: FolderKanban, end: true },
];
const NAV_OVERVIEW = { segment: '', label: 'Overview', icon: BarChart3, end: true };
const NAV_JOBS = { segment: 'jobs', label: 'Agent jobs', icon: Plug, end: false };
const NAV_OUTREACH = [
  { segment: 'leads', label: 'Leads', icon: Users, end: false },
  { segment: 'campaigns', label: 'Campaigns', icon: Send, end: false },
  { segment: 'emails', label: 'Emails', icon: Mail, end: false },
  { segment: 'inbox', label: 'Inbox', icon: Inbox, end: false },
];
const NAV_OUTREACH_SETUP = [
  { segment: 'lead-types', label: 'Lead types', icon: ListChecks, end: false },
  { segment: 'templates', label: 'Email templates', icon: FileText, end: false },
  { segment: 'mailbox', label: 'Mailbox', icon: Settings, end: false },
];
const NAV_CONTENT = [
  { segment: 'topics', label: 'Topics', icon: Lightbulb, end: false },
  { segment: 'posts', label: 'Posts', icon: Newspaper, end: false },
];
const NAV_CONTENT_SETUP = [
  { segment: 'content-channel', label: 'Publishing', icon: Globe, end: false },
];
const navForKind = (kind: ProjectKind) => [
  NAV_OVERVIEW,
  ...(kind === 'CONTENT' ? [...NAV_CONTENT, ...NAV_CONTENT_SETUP] : [...NAV_OUTREACH, ...NAV_OUTREACH_SETUP]),
  NAV_JOBS,
];

const NavItem = ({ to, end, icon: Icon, label }: {
  to: string; end: boolean; icon: typeof Users; label: string;
}) => (
  <NavLink to={to} end={end} className={({ isActive }) => cn(
    'group flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors',
    isActive ? 'bg-brand/[0.08] font-semibold text-brand' : 'font-medium text-muted hover:bg-raised hover:text-ink',
  )}>
    <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
    <span className="truncate">{label}</span>
  </NavLink>
);

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <p className="mb-2 px-3 text-2xs font-medium text-faint">{children}</p>
);

const ProjectSwitcher = ({ currentId }: { currentId?: string }) => {
  const { data: projects, isLoading } = useProjects();
  const current = projects?.find((project) => project.id === currentId);
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" aria-label={`Switch project: ${current?.name ?? 'All projects'}`}
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-line bg-surface p-3 text-left shadow-card transition-colors hover:border-line-strong hover:bg-raised">
          <span className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-xs font-semibold text-brand">
              {current ? initials(current.name) : <FolderKanban className="h-4 w-4" />}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold">{current?.name ?? 'All projects'}</span>
              <span className="block text-xs text-muted">
                {current ? (current.kind === 'CONTENT' ? 'Content project' : 'Outreach project') : isLoading ? 'Loading projects…' : `${projects?.length ?? 0} projects`}
              </span>
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-faint" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={6}
          className="z-[60] max-h-[var(--radix-dropdown-menu-content-available-height)] w-[var(--radix-dropdown-menu-trigger-width)] min-w-56 overflow-y-auto rounded-xl border border-line bg-surface p-1.5 shadow-pop">
          <DropdownMenu.Label className="px-2.5 py-2 text-xs font-medium text-muted">Switch project</DropdownMenu.Label>
          {(projects ?? []).map((project) => (
            <DropdownMenu.Item key={project.id} asChild>
              <Link to={`/projects/${project.id}`} className="flex items-center justify-between gap-3 rounded-lg px-2.5 py-2.5 text-[13px] outline-none data-[highlighted]:bg-raised">
                <span className="truncate">{project.name}</span>
                {project.id === currentId && <Check className="h-4 w-4 shrink-0 text-brand" />}
              </Link>
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="my-1 border-t border-line" />
          <DropdownMenu.Item asChild>
            <Link to="/projects" className="flex rounded-lg px-2.5 py-2.5 text-[13px] font-medium text-brand outline-none data-[highlighted]:bg-raised">View all projects</Link>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export const AppShell = ({ children }: { children: ReactNode }) => {
  const { dark, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const projectId = location.pathname.match(/^\/projects\/([^/]+)/)?.[1];
  const { data: session } = useSession();
  const { data: projects } = useProjects();
  const [mobileOpen, setMobileOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const project = projects?.find((item) => item.id === projectId);
  const projectKind = project?.kind;
  const activeSegment = location.pathname.match(/^\/projects\/[^/]+\/([^/]+)/)?.[1];
  const section = projectId
    ? navForKind(projectKind ?? 'OUTREACH').find((item) => item.segment === (activeSegment ?? ''))?.label
    : location.pathname === '/settings' ? 'Settings' : NAV_GLOBAL.find((item) => item.to === location.pathname)?.label;

  useEffect(() => {
    setMobileOpen(false);
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);
  useEffect(() => {
    document.title = `${section ?? 'Page not found'}${project ? ` · ${project.name}` : ''} · RentX`;
  }, [section, project?.name]);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const closeOnDesktop = () => { if (media.matches) setMobileOpen(false); };
    media.addEventListener('change', closeOnDesktop);
    return () => media.removeEventListener('change', closeOnDesktop);
  }, []);
  useEffect(() => {
    if (!projectId || !projectKind || !activeSegment) return;
    if (!navForKind(projectKind).some((item) => item.segment === activeSegment)) {
      navigate(`/projects/${projectId}`, { replace: true });
    }
  }, [projectId, projectKind, activeSegment, navigate]);

  const projectLinks = (items: typeof NAV_OUTREACH) => items.map((item) => (
    <NavItem key={item.segment} to={`/projects/${projectId}${item.segment ? `/${item.segment}` : ''}`} {...item} />
  ));
  const sidebar = (
    <div className="flex h-full flex-col px-4 py-5">
      <Link to="/" className="mb-7 flex w-fit items-center gap-2.5 px-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand font-display text-lg font-bold text-brand-ink">R</span>
        <span className="font-display text-lg font-semibold tracking-tight">RentX <span className="font-normal text-faint">GTM</span></span>
      </Link>
      <ProjectSwitcher currentId={projectId} />
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto py-6">
        <nav aria-label="Workspace" className="space-y-1">
          <SectionLabel>Workspace</SectionLabel>
          {NAV_GLOBAL.map((item) => <NavItem key={item.to} {...item} />)}
        </nav>
        {projectId && <>
          <nav aria-label="Project work" className="space-y-1">
            <SectionLabel>{projectKind === 'CONTENT' ? 'Content' : 'Outreach'}</SectionLabel>
            {projectLinks([NAV_OVERVIEW, ...(projectKind === 'CONTENT' ? NAV_CONTENT : projectKind ? NAV_OUTREACH : []), NAV_JOBS])}
          </nav>
          {projectKind && <nav aria-label="Project setup" className="space-y-1">
            <SectionLabel>Configuration</SectionLabel>
            {projectLinks(projectKind === 'CONTENT' ? NAV_CONTENT_SETUP : NAV_OUTREACH_SETUP)}
          </nav>}
        </>}
      </div>
      <div className="space-y-3 border-t border-line pt-3">
        <NavItem to="/settings" label="Settings" icon={Settings} end={false} />
        {session && <div className={cn('rounded-lg px-3 py-2.5 text-xs', session.warnings.emailDryRun ? 'bg-warning/[0.07] text-warning' : 'bg-positive/[0.07] text-positive')}>
          <p className="flex items-center gap-2 font-medium"><span className="h-1.5 w-1.5 rounded-full bg-current" />{session.warnings.emailDryRun ? 'Test mode' : 'Live sending'}</p>
          {session.warnings.emailDryRun && <p className="mt-1 pl-3.5 text-[11px]">Emails won’t be delivered.</p>}
        </div>}
        <div className="flex items-center justify-between gap-1 pt-1">
          <Link to="/settings" className="flex min-w-0 items-center gap-2 rounded-lg p-1 hover:bg-raised" title="Your account">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-raised text-xs font-semibold text-muted">{initials(session?.user?.name ?? '?')}</span>
            <span className="truncate text-xs font-medium">{session?.user?.name ?? 'Your account'}</span>
          </Link>
          <div className="flex shrink-0">
            <Tooltip label={dark ? 'Switch to light theme' : 'Switch to dark theme'}>
              <Button variant="ghost" size="icon-sm" onClick={toggle} aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}>{dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</Button>
            </Tooltip>
            <Tooltip label="Sign out"><Button variant="ghost" size="icon-sm" onClick={() => auth.clear()} aria-label="Sign out"><LogOut className="h-4 w-4" /></Button></Tooltip>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
      <div className="flex h-dvh bg-canvas">
        <a href="#main-content" className="sr-only z-[70] rounded-lg bg-brand p-3 text-brand-ink focus:not-sr-only focus:fixed focus:left-4 focus:top-4">Skip to content</a>
        <aside className="hidden w-[248px] shrink-0 border-r border-line bg-surface lg:block">{sidebar}</aside>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-sm" />
          <Dialog.Content aria-describedby={undefined} className="fixed inset-y-0 left-0 z-50 w-[min(300px,90vw)] bg-surface shadow-float">
            <Dialog.Title className="sr-only">Navigation</Dialog.Title>
            <Dialog.Close asChild><Button variant="ghost" size="icon-sm" className="absolute right-3 top-5" aria-label="Close navigation"><X className="h-4 w-4" /></Button></Dialog.Close>
            {sidebar}
          </Dialog.Content>
        </Dialog.Portal>
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-16 shrink-0 items-center gap-3 border-b border-line bg-surface px-4 sm:px-8">
            <Dialog.Trigger asChild><Button variant="ghost" size="icon" className="-ml-2 lg:hidden" aria-label="Open navigation"><Menu className="h-5 w-5" /></Button></Dialog.Trigger>
            <nav aria-label="Breadcrumb" className="min-w-0 text-[13px]">
              <ol className="flex min-w-0 items-center gap-2 text-muted">
                <li className="hidden shrink-0 sm:block"><Link to="/projects" className="hover:text-brand">Workspace</Link></li>
                <ChevronRight className="hidden h-3.5 w-3.5 shrink-0 text-faint sm:block" aria-hidden />
                {project && <><li className="min-w-0 truncate"><Link to={`/projects/${project.id}`} className="hover:text-brand">{project.name}</Link></li><ChevronRight className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden /></>}
                <li aria-current="page" className="shrink-0 font-medium text-ink">{section ?? 'Page not found'}</li>
              </ol>
            </nav>
          </header>
          <main id="main-content" tabIndex={-1} ref={mainRef} className="min-h-0 flex-1 overflow-y-auto focus:outline-none">{children}</main>
        </div>
      </div>
    </Dialog.Root>
  );
};

export const PageHeader = ({ eyebrow, title, description, actions, className }: {
  eyebrow?: ReactNode; title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string;
}) => (
  <div className={cn('mb-7 flex flex-wrap items-center justify-between gap-4', className)}>
    <div className="min-w-0 flex-1 basis-64">
      {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
      <h1 className="break-words text-[26px] font-semibold leading-tight tracking-tight text-ink sm:text-[28px]">{title}</h1>
      {description && <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-muted">{description}</p>}
    </div>
    {actions && <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const Page = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-8 sm:py-8', className)}>{children}</div>
);
